'use client';

import { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  disabled?: boolean;
  className?: string;
}

export function OtpInput({
  value,
  onChange,
  length = 6,
  disabled = false,
  className,
}: OtpInputProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Initialize refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, length);
  }, [length]);

  // Focus first empty input or last input
  useEffect(() => {
    if (value.length < length) {
      const nextIndex = value.length;
      if (inputRefs.current[nextIndex]) {
        inputRefs.current[nextIndex]?.focus();
        setActiveIndex(nextIndex);
      }
    } else {
      setActiveIndex(null);
    }
  }, [value.length, length]);

  const handleChange = (index: number, inputValue: string) => {
    // Only allow single digit
    if (inputValue.length > 1) {
      inputValue = inputValue.slice(-1);
    }

    // Only allow numbers
    if (!/^\d*$/.test(inputValue)) {
      return;
    }

    const newValue = value.split('');
    newValue[index] = inputValue;
    const updatedValue = newValue.join('').slice(0, length);
    onChange(updatedValue);

    // Auto-focus next input
    if (inputValue && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
      setActiveIndex(index + 1);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') {
      if (!value[index] && index > 0) {
        // Move to previous input if current is empty
        inputRefs.current[index - 1]?.focus();
        setActiveIndex(index - 1);
      } else {
        // Clear current input
        const newValue = value.split('');
        newValue[index] = '';
        onChange(newValue.join(''));
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setActiveIndex(index - 1);
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
      setActiveIndex(index + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (pastedData) {
      onChange(pastedData);
      // Focus the next empty input or the last input
      const nextIndex = Math.min(pastedData.length, length - 1);
      inputRefs.current[nextIndex]?.focus();
      setActiveIndex(nextIndex);
    }
  };

  return (
    <div className={cn('flex justify-center gap-2', className)}>
      {Array.from({ length }, (_, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[index] || ''}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={() => setActiveIndex(index)}
          onBlur={() => setActiveIndex(null)}
          disabled={disabled}
          className={cn(
            'h-10 w-10 rounded-lg border-2 text-center text-base font-semibold',
            'focus:ring-primary focus:border-primary focus:ring-2 focus:outline-none',
            'transition-colors duration-200',
            'text-gray-900', // Ensure dark text color for better visibility
            activeIndex === index
              ? 'border-primary bg-primary/10 text-gray-900' // Increased background opacity while maintaining dark text
              : value[index]
                ? 'border-green-500 bg-green-100 text-gray-900' // Increased background opacity while maintaining dark text
                : 'border-gray-300 text-gray-900 hover:border-gray-400',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        />
      ))}
    </div>
  );
}
